import React, {useEffect} from 'react';
import {TextInput, Button, Text, PhoneInput} from '@/ui-kits';
import {useFormik} from 'formik';
import {SignupFormValidation} from '@/utils/schema';
import {useNavigate, useLocation} from 'react-router-dom';
import {Routes} from '@/navigation/Routes';
import {useDispatch} from 'react-redux';
import {signupOtpRequest} from '@/services/redux/slices';
import { getPreSignupFilesToken, createFormattedBlurHandler } from '@/utils/common_functions';
import { PatientSignupOtpRequestPayload } from '@/interface/api-interface';


interface SignupFormInterface {
  email_id: string;
  name: string;
  phone: string;
  location: number;
}

const SignupFormInitialValues: SignupFormInterface = {
  name: '',
  email_id: '',
  phone: '',
  location: -1, // instead of null
};

export const SignupForm: React.FC = () => {
  // ===========================
  // hook
  // ===========================
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get form data from navigation state (when coming back from OTP)
  const navigationState = location.state as { fromOTP?: boolean; formData?: { email_id?: string; phone?: string; name?: string; location?: number } } | null;
  const prefilledData = navigationState?.fromOTP && navigationState.formData ? {
    email_id: navigationState.formData.email_id || '',
    phone: navigationState.formData.phone || '',
    name: navigationState.formData.name || '',
    location: navigationState.formData.location !== undefined ? navigationState.formData.location : -1,
  } : null;
  
  const {values, errors, touched, handleChange, handleBlur, handleSubmit, dirty, isValid, setFieldValue} = useFormik({
    initialValues: prefilledData || SignupFormInitialValues,
    validationSchema: SignupFormValidation,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit,
    enableReinitialize: true,
  });


  const preSignToken = getPreSignupFilesToken();
  
  // ===========================
  // functions
  // ===========================
  const handleNameBlur = createFormattedBlurHandler(handleBlur, setFieldValue, 'name');

  function gotoLoginScreen() {
    navigate(Routes.LOGIN);
  }

  function onSubmit(formValues: SignupFormInterface) {
    const payload: PatientSignupOtpRequestPayload = {
      ...formValues,
    }
    if (preSignToken) {
      payload.pre_signup_token = preSignToken;
    }
    dispatch(signupOtpRequest(payload));
  }

  // ===========================
  // side effects
  // ===========================
  // Clear navigation state after using it to prevent it from persisting
  useEffect(() => {
    if (navigationState?.fromOTP) {
      // Replace current location to remove state from history
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [navigationState, location.pathname]);

  return (
    <div className="flex flex-col gap-4 lg:gap-8 px-4 md:px-8 xl:px-12">
      <Text variant="title" className="lg:mt-8 text-lg font-FigtreeBold text-ui_blue self-center inline-flex gap-1">
        Sign up for
        <p id="logo-text">Second Opinion</p>
      </Text>

      <TextInput
        label="Full Name"
        name="name"
        required
        placeholder="Enter your name"
        value={values.name}
        error={errors.name}
        touched={touched.name}
        onChangeText={handleChange('name')}
        onBlur={handleNameBlur as any}
      />
      <TextInput
        label="Email ID"
        name="email"
        required
        placeholder="Enter your email"
        value={values.email_id}
        error={errors.email_id}
        touched={touched.email_id}
        onChangeText={handleChange('email_id')}
        onBlur={handleBlur('email_id')}
      />
      <PhoneInput
        label="Mobile Number"
        name="phone"
        country={values.location}
        required
        placeholder="Enter mobile number"
        value={values.phone}
        error={errors.phone}
        touched={touched.phone}
        onChange={(phone, country) => {
          setFieldValue('phone', phone, true)
          setFieldValue('location', country, false)
        }}
        onBlur={handleBlur('phone')}
      />

      {/* submit button */}
      <Button
        disabled={!(dirty && isValid)}
        className="w-full items-center py-2 mt-4 lg:mt-0 rounded-lg"
        variant="primary"
        title="Next"
        textClassName="text-xl"
        onClick={() => handleSubmit()}
        rightIcon="arrow_circle_white_right"
      />

      {/* go to log in */}
      <div className="flex flex-col gap-2 items-center mt-1">
        <Text className="text-sm lg:text-base font-Label text-ui_blue opacity-80 self-center" variant="subtitle">
          Already have an account?
        </Text>
        <Text onClick={gotoLoginScreen} className="text-ui_blue font-headingSemiBold cursor-pointer text-xl" variant="subtitle">
          Log In
        </Text>
      </div>
    </div>
  );
};